/**
 * The in-world **Top Banksitters** window: a draggable DOM panel the player opens
 * by clicking the leaderboard bulletin board against the bank's front wall, and
 * closes with a right-click (the classic UO gump gesture). See ADR-0045.
 *
 * It is a sibling overlay of the splash and chat (a fixed-position element layered
 * over the `#app` canvas, not a Pixi in-world gump), so it reuses the same DOM
 * idiom every other piece of britbank chrome uses: plain elements, `textContent`
 * (never `innerHTML`, so a reserved name can't inject markup), and the splash's
 * own `.lb-name`/`.lb-time` row styling. Since Chassis Slice H (PRD #534 D7 /
 * ADR-0271) the window is an instance of the shared {@link createUiWindow}
 * chrome + {@link createTabs} primitives and **owns its DOM**: the markup (same
 * element ids the CSS in `index.html` binds to) is built here at init, not
 * pre-authored in `index.html`.
 *
 * Since Issue #195 the window is **paged**: it browses the *entire* all-time
 * ranking {@link LEADERBOARD_PAGE_SIZE} rows at a time via the paged
 * `GET /leaderboard` read (prev/next + "Page X of Y", the rank numbers carrying
 * on across pages via a CSS `counter-reset` offset), and a **"Jump to your
 * position"** button pages straight to the viewer's own row and highlights it
 * (`.lb-me`) - the target page is computed from the `rank` the server returns for
 * `?me=<name>` in the same round trip, so the full list never ships to the client.
 * Only the **current page** is fetched, on open and then re-polled on a
 * {@link REFRESH_INTERVAL_MS} cadence while the window stays open, so the live
 * refresh stays a fixed ten-row read no matter how deep the all-time board grows;
 * the poll is started on open and cleared on close so no timer or background fetch
 * leaks while the window is shut. Each refresh reconciles the existing rows in
 * place (reusing `<li>`s, writing only changed text) rather than rebuilding the
 * list, so it never flickers or resets the player's scroll, and a stale response
 * (an older in-flight fetch resolving after the player already paged on) is
 * dropped rather than rendered. The human-readable time is the shared
 * {@link formatOnlineTime}, so the window can never drift from the splash panel
 * or the `/top` chat reply.
 *
 * Since Rat Swarm Slice F (PRD #222 / ADR-0121) the window carries a tab set -
 * **Online Time | Gold**, extended with a **Duels** tab (Dueling Leaderboard,
 * ADR-0147), a **Storm Marks** tab (Blackthorn's Storm Slice F, ADR-0153), and an
 * **Arena** tab (Arena Slice E, PRD #334 / ADR-0177). Each non-time tab drives the
 * exact same pager/jump/poll machinery with the leaderboard route's matching mode
 * (`?board=gold`, ADR-0115; `?board=duels`, ADR-0147; `?board=storm`, ADR-0149;
 * `?board=arena`, ADR-0172), rows formatted via the shared {@link formatGold} /
 * {@link formatDuelRecord} (the duel `W-L` string) / {@link formatStormMarks} (the
 * `N Marks` string) / {@link formatArenaScore} (the `N pts` best-run score), while
 * the Online Time tab - the default every open returns to - behaves exactly as
 * before. Switching tabs refetches from page 1; the fetch stamp below also guards
 * cross-board staleness, so a slow page from the previous tab never repaints the new
 * one. Anonymous players never appear on any durable board (reserved-names-only,
 * each stat's Slice A) though they show on the live event boards. The Arena tab
 * additionally wears a **season strip** (arena-seasons): the live season's number
 * plus a toggle onto the most recently concluded season's frozen standings
 * (`?season=last`), with a "no past seasons yet" empty state before any conclude.
 * The **Card Duels** tab defaults to live Elo and uses the same strip to reach its
 * frozen testing W-L record (`?season=legacy`, ADR-0353). It alone is launch-gated:
 * it stays hidden until the server's
 * `cardsEnabled` flag enables it via {@link LeaderboardWindow.setCardsEnabled}
 * (ADR-0300), so a dark-launched card game leaks no leaderboard surface.
 *
 * Like the rest of the client UI it owns only DOM wiring: it reads no world state
 * and sends nothing on the wire - clicking the board is a pure client affordance
 * over a public read-only HTTP read.
 */
import {
  formatArenaScore,
  formatDuelRecord,
  formatElo,
  formatGold,
  formatOnlineTime,
  formatStormMarks,
  LEADERBOARD_PAGE_SIZE,
  type ArenaLeaderboardEntry,
  type CardDuelLeaderboardEntry,
  type LegacyCardDuelLeaderboardEntry,
  type DuelLeaderboardEntry,
  type GoldLeaderboardEntry,
  type LeaderboardBoard,
  type LeaderboardEntry,
  type LeaderboardPage,
  type StormMarksLeaderboardEntry,
} from './leaderboard';
import { createTabs } from './ui/tabs';
import { createUiWindow } from './ui/window';

/** The claim-your-name nudge shown when nobody has reserved a name yet. */
const NO_NAMES_TEXT = 'No names claimed yet — set a passcode to claim yours and start the clock.';
/** The gold board's empty note (Rat Swarm Slice F): the reserve-to-bank-gold nudge. */
const NO_GOLD_TEXT = 'No gold banked yet — reserve a name and stomp some rats in the next swarm.';
/** The duel board's empty note (ADR-0147): the fight-a-duel nudge. */
const NO_DUELS_TEXT = 'No duels fought yet — challenge a rival to open the board.';
/** The Storm Marks board's empty note (ADR-0153): the survive-a-storm nudge. */
const NO_STORM_TEXT = 'No Storm Marks yet — reserve a name and survive Blackthorn’s storm.';
/** The Arena board's empty note (ADR-0177): the run-the-arena nudge. */
const NO_ARENA_TEXT = 'No Arena runs yet — reserve a name and step through the Arena Gate.';
/** The last-season view's empty note (arena-seasons): no season has concluded yet. */
const NO_PAST_SEASONS_TEXT = 'No past seasons yet - the first season is still under way.';
/** The Card Duels board's empty note (ADR-0289): the fight-a-card-duel nudge. */
const NO_CARDS_TEXT = 'No card duels fought yet — challenge a rival to a card duel to open the board.';
const NO_LEGACY_CARDS_TEXT = 'No card duels were recorded during testing.';
/** Shown when the standings fetch fails (server down / network error). */
const UNAVAILABLE_TEXT = 'Standings are unavailable right now.';
/** A row of any board, as the shared render reconciles them. */
type AnyEntry =
  | LeaderboardEntry
  | GoldLeaderboardEntry
  | DuelLeaderboardEntry
  | StormMarksLeaderboardEntry
  | ArenaLeaderboardEntry
  | CardDuelLeaderboardEntry
  | LegacyCardDuelLeaderboardEntry;
/**
 * How often the open window re-polls the current `GET /leaderboard` page. A few
 * seconds keeps the standings visibly live (minutes tick up) without hammering the
 * single-VM server - each poll is one fixed-size page, never the whole board.
 * Exported so the lifecycle test can drive the poll without hard-coding the cadence.
 */
export const REFRESH_INTERVAL_MS = 5_000;

/** Collaborators injected by `main.ts` (kept thin so the window is unit-testable). */
export interface LeaderboardWindowDeps {
  /**
   * Fetch one page of the ranked standings starting at `offset`, or `null` on any
   * failure - `main.ts` passes its `fetchLeaderboardPage`. A non-empty `me` also
   * resolves that viewer's own current rank in the same response (the "Jump to
   * your position" input). `board` selects the ranking (Rat Swarm Slice F): the
   * Online Time default or the Gold board (`?board=gold`, ADR-0115); the window
   * always passes its shown tab. Called on open, tab/page turns, and each poll tick.
   */
  fetchPage: (
    offset: number,
    me?: string,
    board?: LeaderboardBoard,
    season?: 'last' | 'legacy' | number,
  ) => Promise<LeaderboardPage<AnyEntry> | null>;
  /**
   * The viewer's own display name as typed on the splash (empty when not yet
   * entered). Read per fetch, not captured once, so a rejected-login retry with an
   * edited name is picked up automatically.
   */
  viewerName: () => string;
}

/** The handle `main.ts` keeps to open the window from the board click. */
export interface LeaderboardWindow {
  /** Show the window (centring it on first open, back at page 1) and re-fetch the standings. */
  open(): void;
  /** Hide the window. A right-click on the window does this too. */
  close(): void;
  /** Whether the window is currently shown. */
  isOpen(): boolean;
  /**
   * Show/hide the Card Duels tab per the server-authoritative `cardsEnabled` launch
   * flag (ADR-0290/0300, amending ADR-0289's ungated choice): the tab starts hidden
   * and only appears once the Welcome flag enables it, so a dark-launched card game
   * leaks no leaderboard surface. Disabling while the card board is shown falls
   * back to the Online Time default.
   */
  setCardsEnabled(enabled: boolean): void;
}

/** Build the window's content (same ids the CSS binds to) when index.html no longer carries it. */
function ensureContent(doc: Document, root: HTMLElement): void {
  if (doc.getElementById('board-window-list')) return;
  // The board tabs (Rat Swarm Slice F, PRD #222 / ADR-0121): Online Time | Gold |
  // Duels (ADR-0147) | Storm Marks (ADR-0153) | Arena (ADR-0177) | Card Duels (ADR-0289).
  const tabs = doc.createElement('div');
  tabs.id = 'board-window-tabs';
  for (const [id, label, pressed] of [
    ['board-window-tab-time', 'Online Time', 'true'],
    ['board-window-tab-gold', 'Gold', 'false'],
    ['board-window-tab-duels', 'Duels', 'false'],
    ['board-window-tab-storm', 'Storm Marks', 'false'],
    ['board-window-tab-arena', 'Arena', 'false'],
    ['board-window-tab-cards', 'Card Duels', 'false'],
  ] as const) {
    const b = doc.createElement('button');
    b.id = id;
    b.type = 'button';
    b.setAttribute('aria-pressed', pressed);
    b.textContent = label;
    tabs.appendChild(b);
  }
  // The Arena season strip (arena-seasons): visible on the Arena tab only - the
  // live season's number plus a toggle between the live board and the most
  // recently concluded season's frozen standings.
  const season = doc.createElement('div');
  season.id = 'board-window-season';
  season.hidden = true;
  const seasonLabel = doc.createElement('span');
  seasonLabel.id = 'board-window-season-label';
  const seasonToggle = doc.createElement('button');
  seasonToggle.id = 'board-window-season-toggle';
  seasonToggle.type = 'button';
  seasonToggle.textContent = 'View last season';
  season.append(seasonLabel, seasonToggle);
  const body = doc.createElement('div');
  body.id = 'board-window-body';
  const list = doc.createElement('ol');
  list.id = 'board-window-list';
  const empty = doc.createElement('p');
  empty.id = 'board-window-empty';
  empty.hidden = true;
  body.append(list, empty);
  // The pager (Issue #195): browse the whole all-time ranking ten rows at a time,
  // plus a jump straight to (and a highlight on) the viewer's own row.
  const pager = doc.createElement('div');
  pager.id = 'board-window-pager';
  const prev = doc.createElement('button');
  prev.id = 'board-window-prev';
  prev.type = 'button';
  prev.setAttribute('aria-label', 'Previous page');
  prev.textContent = '‹';
  const pageLabel = doc.createElement('span');
  pageLabel.id = 'board-window-page';
  pageLabel.textContent = 'Page 1 of 1';
  const next = doc.createElement('button');
  next.id = 'board-window-next';
  next.type = 'button';
  next.setAttribute('aria-label', 'Next page');
  next.textContent = '›';
  pager.append(prev, pageLabel, next);
  const jump = doc.createElement('button');
  jump.id = 'board-window-jump';
  jump.type = 'button';
  jump.textContent = 'Jump to your position';
  root.append(tabs, season, body, pager, jump);
}

/**
 * Build (or adopt) the `#board-window` overlay as a draggable, right-click-to-close,
 * paged leaderboard window on the shared window/tabs primitives. A body-less
 * document degrades to a harmless no-op handle so a bare client never throws.
 */
export function initLeaderboardWindow(
  doc: Document,
  deps: LeaderboardWindowDeps,
): LeaderboardWindow {
  /** The 0-based rank the shown page starts at (the paging cursor). */
  let offset = 0;
  /** The viewer's own 1-based rank per the latest response (`null` = unranked/unknown). */
  let myRank: number | null = null;
  /**
   * Which Arena board the Arena tab shows (arena-seasons): the live season, or
   * the most recently concluded season's frozen standings (`?season=last`).
   * Reset to `'live'` on every tab change so a fresh Arena visit starts current.
   */
  let arenaSeasonView: 'live' | 'last' = 'live';
  /** Live Elo or the frozen pre-Elo testing record while the Card Duels tab is shown. */
  let cardView: 'elo' | 'legacy' = 'elo';
  /**
   * Monotonic fetch stamp: each refresh takes the next id and only the newest is
   * allowed to render, so an older in-flight page can't overwrite a newer one
   * after a quick prev/next (or a jump) races the 5s poll.
   */
  let fetchSeq = 0;
  let pollTimer: ReturnType<typeof setInterval> | undefined;

  const win = createUiWindow({
    doc,
    id: 'board-window',
    title: 'Top Banksitters',
    ariaLabel: 'Top Banksitters',
    fallbackWidth: 280,
    fallbackHeight: 200,
    onOpen() {
      // Every open starts back at the Online Time tab, page 1 (the podium) - the
      // natural "look at the board" view - then fetches immediately and polls on a
      // steady cadence so the shown page stays live while the window is up. Guard
      // the timer so re-opening an already-open window doesn't stack a second interval.
      tabs.set('time');
      offset = 0;
      refresh();
      pollTimer ??= setInterval(refresh, REFRESH_INTERVAL_MS);
    },
    onClose() {
      // Stop polling the moment the window shuts: no leaked timer, no fetching while
      // closed. This is also the only teardown path (the window lives for the page).
      if (pollTimer !== undefined) {
        clearInterval(pollTimer);
        pollTimer = undefined;
      }
    },
  });
  if (!win) {
    return { open: () => {}, close: () => {}, isOpen: () => false, setCardsEnabled: () => {} };
  }
  ensureContent(doc, win.root);

  const list = doc.getElementById('board-window-list');
  const empty = doc.getElementById('board-window-empty');
  const prevBtn = doc.getElementById('board-window-prev') as HTMLButtonElement | null;
  const nextBtn = doc.getElementById('board-window-next') as HTMLButtonElement | null;
  const pageLabel = doc.getElementById('board-window-page');
  const jumpBtn = doc.getElementById('board-window-jump') as HTMLButtonElement | null;
  const seasonStrip = doc.getElementById('board-window-season');
  const seasonLabel = doc.getElementById('board-window-season-label');
  const seasonToggle = doc.getElementById('board-window-season-toggle') as HTMLButtonElement | null;
  if (
    !list ||
    !empty ||
    !prevBtn ||
    !nextBtn ||
    !pageLabel ||
    !jumpBtn ||
    !seasonStrip ||
    !seasonLabel ||
    !seasonToggle
  ) {
    return { open: () => {}, close: () => {}, isOpen: () => false, setCardsEnabled: () => {} };
  }

  /**
   * Reconcile the ranked rows in place against the latest page. The `name + time`
   * row structure keeps the established `.lb-name`/`.lb-time` cells and the
   * CSS-generated rank number; with paging, the counter is offset via an inline `counter-reset`
   * so page 2 numbers itself 11-20, not 1-10. The viewer's own row (the response
   * `rank`, when it lands on this page) carries `.lb-me` so "you" reads at a glance.
   *
   * Because this runs on every poll (not just open), it reuses the existing `<li>`s
   * and rewrites only the text that changed, then adds/removes the tail - so a live
   * refresh never flickers or resets the player's scroll. `null` (a failed fetch) on
   * the *first* render shows an unavailable note, but a transient poll blip once
   * standings are already on screen leaves them intact rather than blanking the
   * panel out from under the reader.
   */
  const render = (page: LeaderboardPage<AnyEntry> | null): void => {
    const board = tabs.value();
    if (page === null) {
      if (list.childElementCount === 0) {
        empty.textContent = UNAVAILABLE_TEXT;
        empty.hidden = false;
      }
      return;
    }
    myRank = page.rank;
    // Continue the CSS rank numbering across pages: the shared `::before` counter
    // starts from this page's first rank instead of 1.
    list.style.counterReset = `rank ${page.offset}`;
    for (const [i, entry] of page.entries.entries()) {
      let li = list.children[i] as HTMLLIElement | undefined;
      if (!li) {
        li = doc.createElement('li');
        const name = doc.createElement('span');
        name.className = 'lb-name';
        const time = doc.createElement('span');
        time.className = 'lb-time';
        li.append(name, time);
        list.appendChild(li);
      }
      const name = li.querySelector('.lb-name')!;
      const time = li.querySelector('.lb-time')!;
      const nameText = entry.name;
      // The amount cell formats per the shown board (Slice F; ADR-0147): gold rows
      // carry `gold` (formatGold), duel rows `wins`/`losses` (formatDuelRecord →
      // `W-L`), time rows `totalOnlineMs` (formatOnlineTime). The fetch-stamp guard
      // above means a row is only ever rendered under the board it was fetched for,
      // so the current `board` is the right discriminant.
      const timeText =
        board === 'gold'
          ? formatGold((entry as GoldLeaderboardEntry).gold)
          : board === 'duels'
            ? formatDuelRecord(
                (entry as DuelLeaderboardEntry).wins,
                (entry as DuelLeaderboardEntry).losses,
              )
            : board === 'cards'
              ? cardView === 'legacy'
                ? formatDuelRecord(
                    (entry as LegacyCardDuelLeaderboardEntry).wins,
                    (entry as LegacyCardDuelLeaderboardEntry).losses,
                  )
                : formatElo((entry as CardDuelLeaderboardEntry).rating)
              : board === 'storm'
                ? formatStormMarks((entry as StormMarksLeaderboardEntry).stormMarks)
                : board === 'arena'
                  ? formatArenaScore((entry as ArenaLeaderboardEntry).bestScore)
                  : formatOnlineTime((entry as LeaderboardEntry).totalOnlineMs);
      // textContent (never innerHTML) so a reserved name can't inject markup, and
      // guarded so an unchanged cell isn't needlessly rewritten.
      if (name.textContent !== nameText) name.textContent = nameText;
      if (time.textContent !== timeText) time.textContent = timeText;
      // The viewer's own row: rank is 1-based on the whole board, rows 0-based on
      // this page. Toggled every reconcile so the highlight follows rank changes.
      li.classList.toggle('lb-me', page.rank !== null && page.rank - page.offset - 1 === i);
    }
    // Drop any surplus rows left from a now-shorter page (e.g. the last page).
    while (list.childElementCount > page.entries.length) {
      list.removeChild(list.lastElementChild!);
    }
    empty.textContent =
      board === 'gold'
        ? NO_GOLD_TEXT
        : board === 'duels'
          ? NO_DUELS_TEXT
          : board === 'cards'
            ? cardView === 'legacy' ? NO_LEGACY_CARDS_TEXT : NO_CARDS_TEXT
            : board === 'storm'
              ? NO_STORM_TEXT
              : board === 'arena'
                ? arenaSeasonView === 'last' && page.season === null
                  ? NO_PAST_SEASONS_TEXT
                  : NO_ARENA_TEXT
                : NO_NAMES_TEXT;
    empty.hidden = page.total > 0;

    // The Arena season strip (arena-seasons): shown on the Arena tab only. The
    // label reads off the response's season stamp (live: "Season N"; archive:
    // "Season N - final standings"; no archive yet: nothing to name), and the
    // toggle flips between the live board and the frozen one.
    seasonStrip.hidden = board !== 'arena' && board !== 'cards';
    if (board === 'arena') {
      const stamp = page.season;
      seasonLabel.textContent =
        arenaSeasonView === 'last'
          ? typeof stamp === 'number'
            ? `Season ${stamp} - final standings`
            : ''
          : typeof stamp === 'number'
            ? `Season ${stamp}`
            : '';
      seasonToggle.textContent =
        arenaSeasonView === 'last' ? 'Back to current season' : 'View last season';
    } else if (board === 'cards') {
      seasonLabel.textContent = cardView === 'legacy' ? 'Legacy testing record' : 'Elo ratings';
      seasonToggle.textContent =
        cardView === 'legacy' ? 'Back to Elo ratings' : 'View testing record';
    }

    // The pager: "Page X of Y" plus prev/next enablement, all derived from the
    // response (not the request), so a shrunken/grown board self-corrects.
    const pageCount = Math.max(1, Math.ceil(page.total / LEADERBOARD_PAGE_SIZE));
    const pageIndex = Math.min(pageCount - 1, Math.floor(page.offset / LEADERBOARD_PAGE_SIZE));
    pageLabel.textContent = `Page ${pageIndex + 1} of ${pageCount}`;
    prevBtn.disabled = page.offset === 0;
    nextBtn.disabled = page.offset + LEADERBOARD_PAGE_SIZE >= page.total;
    // "Jump to your position" needs a rank to jump to: disabled (with a why) for a
    // viewer who doesn't rank yet - anonymous, or a fresh claim with no banked time.
    jumpBtn.disabled = page.rank === null;
    jumpBtn.title =
      page.rank === null
        ? board === 'gold'
          ? 'Claim a name and stomp some rats to get a position.'
          : board === 'duels'
            ? 'Claim a name and win a duel to get a position.'
            : board === 'cards'
              ? cardView === 'legacy'
                ? 'You have no legacy testing record.'
                : 'Complete a ranked card duel to receive an Elo position.'
              : board === 'storm'
                ? 'Claim a name and survive a storm to get a position.'
                : board === 'arena'
                  ? 'Claim a name and fight an Arena run to get a position.'
                  : 'Claim a name and bank some time to get a position.'
        : `You are #${page.rank} of ${page.total}.`;
  };

  /**
   * Pull the current page once and render it, always asking for the viewer's rank
   * alongside (so the highlight and the jump button stay current). A resolve is
   * ignored when the window has closed meanwhile *or* a newer fetch has been
   * issued (page turn / jump), so stale pages never render. If the shown offset
   * has slid past a shrunken board's end (an empty non-first page), snap back to
   * the last real page and refetch once.
   */
  const refresh = (): void => {
    const seq = ++fetchSeq;
    const requested = offset;
    const board = tabs.value();
    // The Arena tab's last-season view rides the `season` param (arena-seasons);
    // every other read (including the live Arena board) omits it.
    const season =
      board === 'arena' && arenaSeasonView === 'last'
        ? ('last' as const)
        : board === 'cards' && cardView === 'legacy'
          ? ('legacy' as const)
          : undefined;
    void deps.fetchPage(requested, deps.viewerName() || undefined, board, season).then((page) => {
      if (!win.isOpen() || seq !== fetchSeq) return;
      if (page !== null && page.entries.length === 0 && page.total > 0 && requested > 0) {
        offset = Math.floor((page.total - 1) / LEADERBOARD_PAGE_SIZE) * LEADERBOARD_PAGE_SIZE;
        refresh();
        return;
      }
      render(page);
    });
  };

  /** Turn to the page starting at `to` (floored at the first) and fetch it now. */
  const goTo = (to: number): void => {
    offset = Math.max(0, to);
    refresh();
  };

  // The tab strip (Slice F): switch rankings and refetch from page 1. Same-tab
  // clicks are no-ops (the primitive's guard); the fetch-stamp guard in `refresh`
  // means a slow page from the previous tab can never repaint the new one. The
  // viewer's rank is left to the fresh response (each board answers its own rank
  // for ?me=).
  const tabs = createTabs<LeaderboardBoard>(
    [
      { button: doc.getElementById('board-window-tab-time'), value: 'time' },
      { button: doc.getElementById('board-window-tab-gold'), value: 'gold' },
      { button: doc.getElementById('board-window-tab-duels'), value: 'duels' },
      { button: doc.getElementById('board-window-tab-storm'), value: 'storm' },
      { button: doc.getElementById('board-window-tab-arena'), value: 'arena' },
      { button: doc.getElementById('board-window-tab-cards'), value: 'cards' },
    ],
    'time',
    () => {
      // The old board's rank means nothing here: clear it AND disarm the jump
      // button now, so a stale-enabled jump can't fire in the window between the
      // tab switch and the fresh page render (which re-derives both). A fresh
      // Arena visit always starts on the live season (arena-seasons); the strip
      // hides immediately so a non-arena tab never briefly wears it.
      myRank = null;
      jumpBtn.disabled = true;
      arenaSeasonView = 'live';
      cardView = 'elo';
      seasonStrip.hidden = tabs.value() !== 'arena' && tabs.value() !== 'cards';
      goTo(0);
    },
  );

  prevBtn.addEventListener('click', () => goTo(offset - LEADERBOARD_PAGE_SIZE));
  nextBtn.addEventListener('click', () => goTo(offset + LEADERBOARD_PAGE_SIZE));
  // Flip the Arena tab between the live season and last season's frozen standings
  // (arena-seasons), back at page 1 either way - the rank/jump/highlight machinery
  // re-derives from the fresh response, which answers ranks for both views.
  seasonToggle.addEventListener('click', () => {
    if (tabs.value() === 'cards') {
      cardView = cardView === 'legacy' ? 'elo' : 'legacy';
      seasonToggle.textContent =
        cardView === 'legacy' ? 'Back to Elo ratings' : 'View testing record';
    } else {
      arenaSeasonView = arenaSeasonView === 'last' ? 'live' : 'last';
      seasonToggle.textContent =
        arenaSeasonView === 'last' ? 'Back to current season' : 'View last season';
    }
    myRank = null;
    jumpBtn.disabled = true;
    goTo(0);
  });
  // Jump straight to the page holding the viewer's own row (Issue #195): the page
  // is derived from the last response's rank; the fetch it triggers re-resolves the
  // rank, so the highlight is applied from fresh data even if ranks just shifted.
  jumpBtn.addEventListener('click', () => {
    if (myRank === null) return;
    goTo(Math.floor((myRank - 1) / LEADERBOARD_PAGE_SIZE) * LEADERBOARD_PAGE_SIZE);
  });

  // The Card Duels tab follows the server-authoritative `cardsEnabled` launch flag
  // (ADR-0290/0300, amending ADR-0289): hidden until the Welcome flag enables it,
  // exactly like the toolbar Cards button, and hidden again on disable. If the card
  // board is showing when the flag drops, fall back to the Online Time default
  // (silently via `set`, then refetch while open - a closed window resets to `time`
  // on open anyway).
  const cardsTabBtn = doc.getElementById('board-window-tab-cards');
  const setCardsEnabled = (enabled: boolean): void => {
    if (cardsTabBtn) cardsTabBtn.hidden = !enabled;
    if (!enabled && tabs.value() === 'cards') {
      tabs.set('time');
      if (win.isOpen()) goTo(0);
    }
  };
  setCardsEnabled(false);

  return { open: win.open, close: win.close, isOpen: win.isOpen, setCardsEnabled };
}
